/**
 * ApplicationShell — canonical authenticated application layout.
 *
 * Composes ShellHeader (with horizontal tab navigation), breadcrumbs,
 * delegation banner, global notifications, and the main content area. Includes:
 * - Skip-to-main-content link for accessibility.
 * - ARIA landmarks (banner, navigation, main).
 * - Focus restoration on route change (SHELL-004).
 */
import { Box, Toolbar, Link as MuiLink } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import { ShellHeader } from './ShellHeader';
import { DelegationBanner } from './DelegationBanner';
import { GlobalNotifications } from './GlobalNotifications';
import { ShellBreadcrumbs } from './ShellBreadcrumbs';
import { ShellStatusBoundary } from './ShellStatusBoundary';
import { AppContentContainer } from '../layout/AppContentContainer';

export function ApplicationShell() {
  const { t } = useTranslation();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  // SHELL-004: Restore focus to main content on route change.
  useEffect(() => {
    if (mainRef.current && location.pathname !== '/') {
      mainRef.current.focus();
    }
  }, [location.pathname]);

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden',
      }}
      data-testid="app-shell"
    >
      <MuiLink
        href="#main-content"
        sx={{
          position: 'fixed',
          left: 16,
          top: 16,
          transform: 'translateY(calc(-100% - 32px))',
          zIndex: 1300,
          bgcolor: 'background.paper',
          px: 2,
          py: 1,
          borderRadius: 1,
          boxShadow: 3,
          '&:focus': {
            transform: 'translateY(0)',
          },
        }}
        data-testid="skip-to-main-content"
      >
        {t('shell:skipToMainContent')}
      </MuiLink>

      <ShellHeader />

      <Box
        component="main"
        id="main-content"
        ref={mainRef}
        tabIndex={-1}
        data-testid="main-content"
        aria-label={t('shell:mainContentLabel')}
        sx={{
          flexGrow: 1,
          minWidth: 0,
          outline: 'none',
        }}
      >
        <Toolbar />
        <DelegationBanner />
        <AppContentContainer width="full" viewport>
          <ShellBreadcrumbs pathname={location.pathname} />
          <ShellStatusBoundary>
            <Outlet />
          </ShellStatusBoundary>
        </AppContentContainer>
      </Box>

      <GlobalNotifications />
    </Box>
  );
}
